import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { usePrefs } from "@/contexts/PrefsContext";
import { toast } from "sonner";
import {
    Target, Plus, X, Info, CheckSquare, Square, BookMarked,
    Sparkles, ArrowRight,
} from "lucide-react";

/**
 * VAULT · Goals & Life Event Playbooks
 *
 * Goals are user-defined organisational targets — VAULT never recommends
 * a target amount and never claims a goal will be achieved. Playbooks
 * are checklists only, not regulated advice.
 */

const CATEGORIES = [
    "Emergency Reserve", "Travel", "Home", "Education",
    "Vehicle", "Major Purchase", "Retirement Records", "Custom",
];

function GoalCard({ g, hide, onOpen }) {
    const target = Number(g.target_amount || 0);
    const cur    = Number(g.current_amount || 0);
    const pct    = target > 0 ? Math.min(100, Math.round(100 * cur / target)) : null;
    return (
        <button onClick={onOpen}
                className="text-left bg-white border border-stone-200 rounded-2xl p-5 hover:border-stone-500 transition-colors"
                data-testid={`goal-card-${g.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                        {g.category || "Custom"}
                    </div>
                    <div className="text-lg font-display text-stone-900 mt-1">{g.name}</div>
                </div>
                <ArrowRight size={14} className="text-stone-400" />
            </div>
            {target > 0 ? (
                <>
                    <div className="mt-4 h-2 rounded-full bg-stone-100 overflow-hidden">
                        <div className="h-full bg-amber-500"
                             style={{ width: `${pct ?? 0}%` }} />
                    </div>
                    <div className="text-[11.5px] text-stone-600 mt-1.5 flex items-center justify-between">
                        <span className="tabular-nums">
                            {hide ? "•••••" : fmtINR(cur)} of {hide ? "•••••" : fmtINR(target)}
                        </span>
                        <span className="tabular-nums">{pct ?? 0}%</span>
                    </div>
                </>
            ) : (
                <div className="mt-4 text-[11.5px] text-stone-500">No target amount set — organisational only.</div>
            )}
            {g.target_date && (
                <div className="text-[11px] text-stone-500 mt-1">Target · {g.target_date}</div>
            )}
        </button>
    );
}

function GoalModal({ open, initial, onClose, onSave, onDelete }) {
    const [form, setForm] = useState({
        name: "", category: "Custom", target_amount: "", target_date: "",
        current_amount: "", notes: "",
    });
    useEffect(() => {
        if (!open) return;
        setForm(initial ? {
            name: initial.name || "", category: initial.category || "Custom",
            target_amount: initial.target_amount ?? "", target_date: initial.target_date || "",
            current_amount: initial.current_amount ?? "", notes: initial.notes || "",
        } : { name: "", category: "Custom", target_amount: "", target_date: "", current_amount: "", notes: "" });
    }, [open, initial]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="goal-modal">
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
                <div className="bg-stone-900 text-white px-6 py-4 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-400 mb-1">
                            {initial ? "Edit goal" : "New goal"}
                        </div>
                        <div className="text-xl font-display">Track a target you care about.</div>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-white"><X size={16} /></button>
                </div>
                <div className="p-6 space-y-3">
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Name
                        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="goal-name" />
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Category
                        <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                                className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                                data-testid="goal-category">
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Target ₹
                            <input type="number" value={form.target_amount}
                                   onChange={e => setForm({...form, target_amount: e.target.value})}
                                   className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                                   data-testid="goal-target-amount" />
                        </label>
                        <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Target date
                            <input type="date" value={form.target_date}
                                   onChange={e => setForm({...form, target_date: e.target.value})}
                                   className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                                   data-testid="goal-target-date" />
                        </label>
                    </div>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Recorded progress ₹
                        <input type="number" value={form.current_amount}
                               onChange={e => setForm({...form, current_amount: e.target.value})}
                               className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                               data-testid="goal-current-amount" />
                    </label>
                    <label className="block text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Notes
                        <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                                  rows={2}
                                  className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
                                  data-testid="goal-notes" />
                    </label>
                    <div className="text-[11px] text-stone-500 flex items-start gap-1">
                        <Info size={11} className="mt-0.5" />
                        VAULT never recommends a target amount and never claims a goal will be achieved.
                    </div>
                </div>
                <div className="border-t border-stone-200 px-6 py-4 bg-stone-50 flex items-center justify-between gap-2">
                    <div>
                        {initial && (
                            <button onClick={() => onDelete(initial)}
                                    className="text-[12px] font-semibold text-rose-700 hover:text-rose-900"
                                    data-testid="goal-delete">Delete</button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose}
                                className="px-4 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500">
                            Cancel
                        </button>
                        <button onClick={() => {
                                    if (!form.name) { toast.error("Name is required"); return; }
                                    onSave({
                                        ...form,
                                        target_amount: form.target_amount ? Number(form.target_amount) : null,
                                        current_amount: form.current_amount ? Number(form.current_amount) : 0,
                                    });
                                }}
                                className="px-5 py-2 rounded-full bg-amber-500 text-stone-900 text-[13px] font-bold hover:bg-amber-400"
                                data-testid="goal-save">
                            Save goal
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PlaybookRow({ pb, onToggle, onOpen }) {
    const total = pb.sections?.reduce((n, s) => n + (s.items?.length || 0), 0) || 0;
    const done  = pb.sections?.reduce((n, s) => n + (s.items?.filter(i => i.done).length || 0), 0) || 0;
    const pct   = total > 0 ? Math.round(100 * done / total) : 0;
    return (
        <div className="border-b border-stone-100 last:border-0 px-4 py-3" data-testid={`playbook-${pb.id}`}>
            <button onClick={() => onOpen(pb)} className="w-full text-left flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                        {pb.source === "template" ? "From template" : "Custom"}
                    </div>
                    <div className="text-[13.5px] font-semibold text-stone-900 truncate mt-0.5">{pb.name}</div>
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[13px] font-display tabular-nums text-stone-900">{done}/{total}</div>
                    <div className="text-[10.5px] text-stone-500">{pct}% done</div>
                </div>
            </button>
        </div>
    );
}

function PlaybookDrawer({ pb, onClose, onToggle }) {
    if (!pb) return null;
    return (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
             data-testid="playbook-drawer">
            <div className="bg-white rounded-t-2xl md:rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
                <div className="bg-stone-900 text-white px-6 py-4 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-400 mb-1">Playbook</div>
                        <div className="text-xl font-display">{pb.name}</div>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-white"><X size={16} /></button>
                </div>
                <div className="p-6 space-y-5 overflow-y-auto">
                    {(pb.sections || []).map((s, i) => (
                        <div key={`${pb.id}-s-${i}`}>
                            <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">
                                {s.title}
                            </div>
                            <ul className="space-y-1.5">
                                {(s.items || []).map(it => {
                                    const Icon = it.done ? CheckSquare : Square;
                                    return (
                                        <li key={it.id}>
                                            <button onClick={() => onToggle(pb, i, it)}
                                                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone-50 ${it.done ? "text-stone-500 line-through" : "text-stone-900"}`}
                                                    data-testid={`playbook-item-${it.id}`}>
                                                <Icon size={14} className={it.done ? "text-emerald-600" : "text-stone-400"} />
                                                <span className="text-[13px]">{it.title}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                    <div className="text-[11px] text-stone-500 flex items-start gap-1">
                        <Info size={11} className="mt-0.5" />
                        Life Event Playbooks are organisational checklists only. They do not provide legal, tax, investment or regulated financial advice.
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Goals() {
    const { prefs } = usePrefs();
    const hide = !!prefs?.hide_balance;
    const [tab, setTab] = useState("goals");
    const [goals, setGoals] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [playbooks, setPlaybooks] = useState([]);
    const [modalGoal, setModalGoal] = useState(null);
    const [showGoalModal, setShowGoalModal] = useState(false);
    const [activePb, setActivePb] = useState(null);

    const load = async () => {
        try {
            const [g, tpl, pb] = await Promise.all([
                api.get("/goals"),
                api.get("/playbooks/templates"),
                api.get("/playbooks"),
            ]);
            setGoals(g.data || []); setTemplates(tpl.data?.templates || []); setPlaybooks(pb.data || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load goals"); }
    };
    useEffect(() => { load(); }, []);

    const saveGoal = async (payload) => {
        try {
            if (modalGoal) {
                await api.patch(`/goals/${modalGoal.id}`, payload);
                toast.success("Goal updated");
            } else {
                await api.post("/goals", payload);
                toast.success("Goal created");
            }
            setShowGoalModal(false); setModalGoal(null); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    };
    const deleteGoal = async (g) => {
        try { await api.delete(`/goals/${g.id}`); toast.success("Deleted"); setShowGoalModal(false); setModalGoal(null); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
    };

    const instantiate = async (slug) => {
        try { const r = await api.post(`/playbooks/instantiate/${slug}`); toast.success("Playbook added"); load(); setActivePb(r.data); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add playbook"); }
    };

    const togglePbItem = async (pb, section_index, item) => {
        try {
            const r = await api.post(`/playbooks/${pb.id}/toggle-item`, {
                section_index, item_id: item.id, done: !item.done,
            });
            setActivePb(r.data);
            setPlaybooks(pbs => pbs.map(p => p.id === pb.id ? r.data : p));
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update item"); }
    };

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="goals-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <Target size={12} /> VAULT · Goals
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Your targets, your way.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Track any target you care about. Attach a related account or investment for a live view.
                        VAULT never recommends target amounts or predicts outcomes.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => { setModalGoal(null); setShowGoalModal(true); }}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                            data-testid="goal-new">
                        <Plus size={13} /> New goal
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-1 border border-stone-200 rounded-full p-1 bg-white w-fit mb-6"
                 data-testid="goals-tabs">
                {[["goals", "Goals"], ["playbooks", "Life Event Playbooks"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                            className={`px-4 py-1.5 text-[12.5px] font-semibold rounded-full transition-colors ${
                                tab === k ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"
                            }`}
                            data-testid={`goals-tab-${k}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === "goals" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="goals-grid">
                    {goals.length === 0 ? (
                        <div className="col-span-full text-center text-sm text-stone-400 py-16">
                            No goals yet. Create your first target.
                        </div>
                    ) : goals.map(g => (
                        <GoalCard key={g.id} g={g} hide={hide}
                                  onOpen={() => { setModalGoal(g); setShowGoalModal(true); }} />
                    ))}
                </div>
            )}

            {tab === "playbooks" && (
                <>
                    <div className="mb-6">
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2 flex items-center gap-1">
                            <Sparkles size={11} /> Choose a template
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {templates.map(tpl => (
                                <button key={tpl.slug} onClick={() => instantiate(tpl.slug)}
                                        className="text-left bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-500 transition-colors"
                                        data-testid={`playbook-template-${tpl.slug}`}>
                                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Template</div>
                                    <div className="text-[13.5px] font-semibold text-stone-900 mt-1">{tpl.name}</div>
                                    <div className="text-[11px] text-stone-500 mt-1">
                                        {tpl.sections?.length || 0} sections
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="playbooks-list">
                        <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
                            <BookMarked size={13} className="text-stone-500" />
                            <h2 className="text-lg font-display text-stone-900">Your playbooks</h2>
                        </div>
                        {playbooks.length === 0 ? (
                            <div className="p-8 text-center text-sm text-stone-400">
                                Pick a template above to get started.
                            </div>
                        ) : playbooks.map(pb => (
                            <PlaybookRow key={pb.id} pb={pb} onOpen={setActivePb} />
                        ))}
                    </div>
                </>
            )}

            <GoalModal open={showGoalModal} initial={modalGoal}
                       onClose={() => { setShowGoalModal(false); setModalGoal(null); }}
                       onSave={saveGoal} onDelete={deleteGoal} />
            <PlaybookDrawer pb={activePb} onClose={() => setActivePb(null)} onToggle={togglePbItem} />
        </div>
    );
}
